import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import RainbowLoader from '@components/Loaders/RainbowLoader';
import OnScreenDebugger from '@utils/OnScreenDebuuger';
import db from '@utils/db';
import { Container } from './QR.styled';
import { useStore } from '@store/appStore';
import { shallow } from 'zustand/shallow';
import type { IScannerProps } from '@yudiel/react-qr-scanner';

const Scanner = dynamic<IScannerProps>(() => import('@yudiel/react-qr-scanner').then((m) => m.Scanner), { ssr: false });

interface iProps {
  GetInformation: (QRCode: string) => Promise<boolean>;
  error: (error: number, details?: { name?: string; message?: string }) => void;
  swapCamera?: () => void;
  returnResult: (QRCode: string) => void;
}

const QR = forwardRef((props: iProps, ref) => {
  const timeOut = useRef(null);
  const [cameraPermission, setCameraPermission] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true)
  
  const {
    currentCam,
    setCurrentCam,
    setCameraList,
    cameraList,
    cameraInfo,
    setCameraInfo
  } = useStore(
    (state) => ({
      currentCam: state.currentCam,
      setCurrentCam: state.setCurrentCam,
      setCameraList: state.setCameraList,
      cameraList: state.cameraList,
      cameraInfo: state.cameraInfo,
      setCameraInfo: state.setCameraInfo
    }),
    shallow,
  );  

  const memoizedCam = useMemo(() => {
    return <Scanner onScan={(result) => ReturnQRCode(result)} constraints={{ deviceId: cameraInfo?.id }} />;
}, [cameraInfo?.id]);

const memoizedErrorCam = useMemo(() => {
  return <Scanner onScan={(result) => ReturnQRCode(result)} constraints={{
    facingMode: 'environment',
  }} />;
}, []);

  useImperativeHandle(ref, () => ({
    swapCamera() {
      if (cameraList.length > 0) {
        let c = currentCam
        if (cameraList.length - 1 > c) c++;
        else c = 0;
        setCameraInfo(cameraList[c])
        setCurrentCam(c)
        localStorage.setItem('currentCam', c)
      }
    },
  }));


  useEffect(() => {
    checkPermission();
    return () => clearTimeout(timeOut.current);
  }, []);


  useEffect(() => {
    if (cameraPermission) {
      getCameras();
    }
  }, [cameraPermission]);

  const checkPermission = () => {
    const permissions = navigator.mediaDevices.getUserMedia({ video: true });
    permissions.then((stream) => {
      stream.getTracks().forEach((x) => x.stop());
      setCameraPermission(true);
    })
      .catch((error) => {
        setCameraPermission(false);
        // By the error's name - the same in every browser. The messages aren't: Safari/iOS words a
        // refused permission differently from Chrome, and it used to end up as "no camera found".
        const details = { name: error?.name, message: error?.message };
        if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError' || error?.message === 'Permission denied') {
          props.error(1, details);
        } else if (error?.name === 'NotReadableError' || error?.name === 'AbortError' || error?.message === 'Could not start video source') {
          props.error(2, details);
        } else {
          props.error(3, details);
        }
      });
  };

  const getCameras = async () => {
    let caminfo: any;
    let currentCam: any = localStorage.getItem('currentCam')
    if (currentCam) currentCam = Number(currentCam)
    let cameras = []

    if (cameras.length === 0) {
      let devices = await navigator.mediaDevices.enumerateDevices();
      let videodevices = [];
      for (var i = 0; i < devices.length; i++) {
        var device = devices[i];
        if (device.kind === 'videoinput') {
          videodevices.push(device);
        }
      }

      if (videodevices.length > 0) {
        for (var i = 0; i < videodevices.length; i++) {
          var videoDevice = videodevices[i];

          let camtemp = {
            id: videoDevice.deviceId,
          };
          cameras.push(camtemp)
          if ((currentCam === null || currentCam === undefined) && videoDevice.label.toLowerCase().includes('back')) currentCam = i
        }

        if (currentCam) {
          caminfo = cameras[currentCam]
        }
        else {
          currentCam = 0
          caminfo = cameras[currentCam]
        }
        setCameraInfo(cameras[cameras.length - 1]);

        if (caminfo) {          
          setTimeout(function () {
            setCameraInfo(caminfo);
            setLoading(false);
          }, 1000) 

        }

        if (cameras.length > 0) {
          setCameraList(cameras)
          setCurrentCam(currentCam)
        }
      }
    }
  };

  const ReturnQRCode = (result) => {
    props.returnResult(result[0].rawValue)
  }

  return (loading?
    <RainbowLoader />
    :
    <Container>      
      {cameraPermission && cameraInfo && cameraInfo.id ? (
        
        memoizedCam
      ) : error ? (
        memoizedErrorCam
      ) : (
        <RainbowLoader />
      )}
    </Container>
  );
});

export default QR;
